import { useEffect, useRef } from "react";
import * as THREE from "three";

// A cinematic point-cloud sphere that slowly rotates, reacts to the pointer,
// and morphs/disperses as the user scrolls — dissolving into the next section.
export const ThreeHero = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 100);
    camera.position.z = 6;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    // --- Build point cloud on a fibonacci sphere ---
    const COUNT = 5200;
    const radius = 2.35;
    const spherePos = new Float32Array(COUNT * 3);
    const scatterPos = new Float32Array(COUNT * 3);
    const current = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);

    const tealCol = new THREE.Color("#00f0ff");
    const emberCol = new THREE.Color("#f5a524");

    for (let i = 0; i < COUNT; i++) {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / COUNT);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      spherePos.set([x, y, z], i * 3);
      current.set([x, y, z], i * 3);
      // scattered target — pushed outward & downward for the "morph away" feel
      const s = 2.4 + Math.random() * 3.5;
      scatterPos.set([x * s, y * s - 4.5, z * s], i * 3);
      // ~22% warm ember points for cinematic depth, rest teal
      const c = Math.random() < 0.22 ? emberCol : tealCol;
      colors.set([c.r, c.g, c.b], i * 3);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(current, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    const sprite = makeDotTexture();
    const material = new THREE.PointsMaterial({
      size: 0.045,
      map: sprite,
      transparent: true,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.9,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // faint inner core wireframe for depth
    const wire = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.55, 1),
      new THREE.MeshBasicMaterial({ color: 0x0a5c66, wireframe: true, transparent: true, opacity: 0.18 })
    );
    scene.add(wire);

    const group = new THREE.Group();
    group.add(points);
    group.add(wire);
    scene.add(group);

    // --- interaction + scroll state ---
    let pointer = { x: 0, y: 0 };
    let targetRot = { x: 0, y: 0 };
    let progress = 0; // 0 at top → 1 when hero scrolled away

    const onPointer = (e) => {
      pointer.x = (e.clientX / window.innerWidth - 0.5) * 2;
      pointer.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    const onScroll = () => {
      const h = window.innerHeight;
      progress = Math.min(1, Math.max(0, window.scrollY / (h * 0.9)));
    };
    window.addEventListener("pointermove", onPointer);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const clock = new THREE.Clock();
    let raf;
    const animate = () => {
      const t = clock.getElapsedTime();

      // auto spin + pointer parallax
      targetRot.y += 0.0016;
      group.rotation.y += (targetRot.y + pointer.x * 0.4 - group.rotation.y) * 0.05;
      group.rotation.x += (pointer.y * 0.25 - group.rotation.x) * 0.05;

      // morph: lerp each point between sphere and scattered target by progress
      const pos = geometry.attributes.position.array;
      const eased = progress * progress * (3 - 2 * progress); // smoothstep
      for (let i = 0; i < COUNT; i++) {
        const j = i * 3;
        const breathe = 1 + Math.sin(t * 0.6 + i) * 0.015;
        const sx = spherePos[j] * breathe;
        const sy = spherePos[j + 1] * breathe;
        const sz = spherePos[j + 2] * breathe;
        pos[j] = sx + (scatterPos[j] - sx) * eased;
        pos[j + 1] = sy + (scatterPos[j + 1] - sy) * eased;
        pos[j + 2] = sz + (scatterPos[j + 2] - sz) * eased;
      }
      geometry.attributes.position.needsUpdate = true;

      material.opacity = 0.9 * (1 - eased * 0.95);
      wire.material.opacity = 0.18 * (1 - eased);
      camera.position.z = 6 + eased * 2.5;

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      geometry.dispose();
      material.dispose();
      sprite.dispose();
      wire.geometry.dispose();
      wire.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0 h-full w-full" aria-hidden />;
};

// soft circular dot texture for round particles
function makeDotTexture() {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.4, "rgba(255,255,255,0.85)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  return tex;
}

export default ThreeHero;
